import cn from "classnames"
import { useRef } from "react"

import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Text,
  Title
} from "@/atoms"
import {
  TICKET_MODAL_FIELD_KEYS,
  TICKET_MODAL_Z_INDEX
} from "@/const/ticketing.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { InformationAlert } from "@/molecules/InformationAlert"
import { TicketingSystemLogo } from "@/molecules/TicketingSystemLogo"
import { DynamicTagsFormItem } from "../DynamicTagsField"

import type { TicketModalProps } from "./TicketModal.types"

import { useEffect } from "react"
import styles from "./TicketModal.module.scss"

const TRANSLATION_PREFIXES = {
  jira: { ticket: "jiraIssueModal", template: "jiraTemplateModal" },
  vendor: { ticket: "ticketModal", template: "ticketModal" }
} as const

export const TicketModal = ({
  system = "jira",
  mode,
  type,
  metadataIsLoading,
  fieldsIsLoading = false,
  formError,
  onCancel,
  onSubmit,
  submitDisabled,
  url,
  form,
  projects,
  issueTypesOptions,
  onFieldsChange,
  onProjectChange,
  initialSummary,
  initialDescription,
  createServerErrorRule,
  children,
  tags,
  footer,
  previewMode,
  setPreviewMode,
  initialValues,
  fixedProjectLabel
}: TicketModalProps) => {
  const kind = system === "jira" ? "jira" : "vendor"
  const { t } = useTranslation("translation", {
    keyPrefix: TRANSLATION_PREFIXES[kind][mode]
  })
  const { t: tHeader } = useTranslation("translation", {
    keyPrefix:
      kind === "jira"
        ? TRANSLATION_PREFIXES.jira[mode]
        : `ticketModal.${mode}.${system}`
  })
  const fieldKeys = TICKET_MODAL_FIELD_KEYS[kind]
  const isProjectFixed = fixedProjectLabel !== undefined
  const showProject = !isProjectFixed || !!projects.length
  const getServerErrorRules = (name: string, visible: boolean) =>
    createServerErrorRule && visible ? [createServerErrorRule(name)] : undefined
  const { t: tGeneral } = useTranslation("translation", {
    keyPrefix: "general"
  })

  const submitButtonText =
    mode === "ticket" ? tGeneral("create") : tGeneral("save")

  const scrollToTop = (options?: ScrollToOptions) =>
    document.querySelector(`.${styles.modal} .ant-modal-body`)?.scrollTo({
      top: 0,
      ...options
    })

  useEffect(() => {
    scrollToTop({ behavior: "smooth" })
  }, [previewMode])

  const isError = !!formError

  useEffect(() => {
    if (isError) setPreviewMode?.(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  const el = useRef<HTMLElement | undefined>()

  if (!el.current)
    el.current =
      (document
        .querySelector(`.${styles.modal}`)
        ?.querySelector(".ant-modal-body") as HTMLElement) || null

  const hasScrollbar = el.current
    ? el.current.offsetWidth - el.current.clientWidth > 0
    : false

  return (
    <Modal
      destroyOnHidden
      zIndex={TICKET_MODAL_Z_INDEX}
      maskClosable={false}
      className={cn(
        styles.modal,
        mode !== "template" && styles.modalIssueTextEditor,
        metadataIsLoading && styles.isLoading,
        hasScrollbar && styles.hasScrollbar
      )}
      open={type === system}
      width={650}
      onCancel={onCancel}
      title={
        <div className={styles.header}>
          <TicketingSystemLogo system={system} className={styles.icon} />
          <div className={styles.textSection}>
            <Title level={5}>{tHeader("title")}</Title>
            {!metadataIsLoading && url && (
              <Text type="secondary">
                {`${tHeader("description")}`} <a href={url}>{url}</a>
              </Text>
            )}
          </div>
        </div>
      }
      footer={() => (
        <Flex
          className={styles.footerWrapper}
          justify={footer ? "space-between" : "flex-end"}
        >
          {footer}
          <Flex gap={8} align="center">
            <Button key="cancel" onClick={onCancel}>
              {tGeneral("cancel")}
            </Button>
            <Button
              key="submit"
              htmlType="submit"
              type="primary"
              disabled={submitDisabled}
              onClick={onSubmit}
            >
              {submitButtonText}
            </Button>
          </Flex>
        </Flex>
      )}
    >
      <Form
        onFieldsChange={onFieldsChange}
        className={styles.form}
        layout="vertical"
        form={form}
        initialValues={initialValues}
      >
        {isError && (
          <InformationAlert
            type="error"
            message={formError}
            className={cn(
              styles.formErrorContainer,
              previewMode && styles.formErrorContainerPreview
            )}
          />
        )}
        <div className={styles.itemForm}>
          <Form.Item
            className={styles.selectSection}
            name={fieldKeys.project}
            hidden={!showProject}
            label={
              <Text className={styles.label}>
                {fixedProjectLabel ?? t("projectField")}
              </Text>
            }
            rules={getServerErrorRules(fieldKeys.project, showProject)}
          >
            <Select
              disabled={previewMode || isProjectFixed}
              fieldNames={{ value: "id", label: "name" }}
              options={projects}
              filterOption={(input, option) => {
                if (!option) return false
                return (option.name || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }}
              showSearch
              onChange={onProjectChange}
            />
          </Form.Item>
        </div>

        <div className={styles.itemForm}>
          <Form.Item
            className={styles.selectSection}
            name={fieldKeys.type}
            hidden={isProjectFixed}
            label={<Text className={styles.label}>{t("issueTypeField")}</Text>}
            initialValue={isProjectFixed ? undefined : {}}
            rules={getServerErrorRules(fieldKeys.type, !isProjectFixed)}
          >
            <Select
              disabled={previewMode}
              fieldNames={{ value: "id", label: "name" }}
              options={issueTypesOptions}
            />
          </Form.Item>
        </div>

        {showProject && <Divider className={styles.divider} />}

        {mode === "ticket" && (
          <Form.Item
            className={styles.itemForm}
            label={<Text className={styles.label}>{t("summaryField")}</Text>}
            rules={
              createServerErrorRule
                ? [
                    { required: true, message: t("error.summary") || "" },
                    createServerErrorRule(fieldKeys.title)
                  ]
                : [{ required: true, message: t("error.summary") || "" }]
            }
            name={fieldKeys.title}
          >
            <Input disabled={previewMode} />
          </Form.Item>
        )}
        {mode === "template" && (
          <DynamicTagsFormItem
            classes={{ preview: styles.summaryPreview }}
            type="input"
            previewMode={previewMode}
            tags={tags}
            form={form}
            name={fieldKeys.title}
            label={t("summaryField")}
            initialValue={
              form.getFieldValue(fieldKeys.title) || initialSummary || ""
            }
            rules={
              createServerErrorRule
                ? [createServerErrorRule(fieldKeys.title)]
                : undefined
            }
          />
        )}
        <DynamicTagsFormItem
          classes={{ preview: styles.descriptionPreview }}
          previewMode={previewMode}
          tags={tags}
          form={form}
          name={fieldKeys.description}
          label={t("descriptionField")}
          initialValue={initialDescription || ""}
          rules={
            createServerErrorRule
              ? [createServerErrorRule(fieldKeys.description)]
              : undefined
          }
        />
        {fieldsIsLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          children
        )}
      </Form>
    </Modal>
  )
}
